import React, { useState } from 'react';
import { Box, Container, Heading, SimpleGrid } from '@hover/blueprint';
import { DownloadAppModal } from '@hover/shared-components';
import { navigate } from 'gatsby';
import { Helmet } from 'react-helmet';
import { StaticImage } from 'gatsby-plugin-image';
import { useMediaQuery } from '@chakra-ui/react';
import { Layout, UtilityMenuBanner } from '../../../v2/components/common';
import { usePageViewTracking } from '../../../utils/trackPageView';
import {
  HeroVideo,
  CustomerQuote,
  CustomerInfo,
  TryHover,
  SeeFirstHand,
  VideoModal,
  FadeInImage,
  BackButton,
} from '../../../v2/components/customer-stories';
import { useABTesting } from '../../../v2/hooks';
import { ctaABTesting } from '../../../v2/utils/ctaABTesting';

import thumbnail1 from '../../../v2/assets/images/customer_sprague_thumb_1.png';
import thumbnail2 from '../../../v2/assets/images/customer_sprague_thumb_2.png';
import thumbnail3 from '../../../v2/assets/images/customer_sprague_thumb_3.png';
import { spragueVideoLinks } from '../../../v2/constants/customer-stories';

const CustomerStorySprague: React.FC = () => {
  usePageViewTracking('customer-sprague');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState<boolean>(false);
  const [isVideoVertical, setIsVideoVertical] = useState<boolean>(false);
  const [videoLink, setVideoLink] = useState<string>('');

  const [isDesktop] = useMediaQuery('(min-width: 1280px)');
  const abTestValue = useABTesting({ testName: 'measure-page-cta-pressed' });

  if (!abTestValue) return <>Loading...</>;

  const toggleModal = () => {
    if (!isDesktop) setIsOpen(!isOpen);
    else if (abTestValue === 'A') {
      ctaABTesting('A');
      setIsOpen(!isOpen);
    } else {
      ctaABTesting('B');
      void navigate('/sign-up');
    }
  };

  const handleOpenVideoModal = (link: string, isVertical?: boolean) => {
    setVideoLink(link);
    setIsVideoModalOpen(true);
    setIsVideoVertical(Boolean(isVertical));
  };

  const handleCloseVideoModal = () => {
    setVideoLink('');
    setIsVideoModalOpen(false);
    setIsVideoVertical(false);
  };

  return (
    <Layout
      // @memo: title and meta description need to be updated
      title="Customer Story - Sprague Roofing"
      description="Build your business with HOVER. Accurate measurements, fast estimates, direct orders, and happier customers mean more time and money to build your business."
      buttonType="SCHEDULE_A_DEMO"
      customBgColor="#181818"
      customFooterBg="#101010"
      bannerElement={<UtilityMenuBanner />}
    >
      <Helmet>
        <script
          src="https://fast.wistia.com/embed/medias/q7wx946hq2.jsonp"
          async
        />
        <script src="https://fast.wistia.com/assets/external/E-v1.js" async />
      </Helmet>
      <DownloadAppModal isOpen={isOpen} onClose={toggleModal} />
      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={handleCloseVideoModal}
        videoLink={videoLink}
        videoTitle="Sprague Roofing"
        isVertical={isVideoVertical}
      />
      <HeroVideo
        videoLink="https://fast.wistia.net/embed/iframe/jsps7s57uo"
        videoId="jsps7s57uo"
        mobileImage={
          <StaticImage
            src="../../../v2/assets/images/customer_sprague_bg_thumb.png"
            alt="Sprague Roofing's introduction"
            style={{ flexGrow: 1 }}
          />
        }
        handleOpenVideoModal={handleOpenVideoModal}
      />
      <CustomerInfo
        name="Sprague Roofing and Exteriors"
        location="Fort Collins, CO"
        founded="2010"
      />
      <Container
        maxW={{ base: '100%', tablet: '4xl', desktop: '6xl' }}
        px={{ base: 6, tablet: 12 }}
        textAlign="left"
        alignItems="center"
        mb={{ base: 900, desktop: 1000 }}
        mx={0}
        mt="4rem"
      >
        <Heading
          fontWeight={{ base: 400, tablet: 700 }}
          fontSize={{ base: '26px', tablet: '36px' }}
          color="neutral.0"
          lineHeight={{ base: '140%', tablet: '48px' }}
          marginBottom={{ base: 16, desktop: 20 }}
        >
          The Calm After the Storm: Sprague Roofing and Exteriors Helps Property
          Owners Get Back to Normal After a Damaging Storm
        </Heading>

        {/* ContentGrid Start */}
        <SimpleGrid
          columns={{ base: 1, desktop: 2 }}
          spacing={{ base: 500, desktop: 100 }}
          alignItems="center"
          gap={{ base: 12, tablet: 24 }}
          mb={{ base: 900, desktop: 1000 }}
        >
          <Box flexDir="column">
            <Heading
              as="p"
              marginBottom={500}
              isDisplay
              size={200}
              color="neutral.0"
            >
              Like many construction companies, Sprague Roofing and Exteriors is
              contending with the trifecta of labor shortages, supply chain
              disruptions, and a shaky economy. In order to protect margins and
              win new business in this challenging and hyper-competitive
              environment, the full-scope exteriors contractor has put accuracy,
              efficiency, and service front and center.
            </Heading>
            <Heading
              marginTop={200}
              marginBottom={500}
              isDisplay
              size={400}
              fontWeight={600}
              color="neutral.0"
            >
              Letting the Customer Know They’re in Good Hands
            </Heading>
            <Heading
              as="p"
              marginBottom={500}
              isDisplay
              size={200}
              color="neutral.0"
            >
              “The economy is tough, and a lot of people aren’t spending as much
              as they used to on their homes or businesses,” says Josh Sierra,
              General Manager of Sprague’s Fort Collins, Colorado office.
            </Heading>
            <Heading
              as="p"
              marginBottom={500}
              isDisplay
              size={200}
              color="neutral.0"
            >
              “You need to do everything you can to combat that and win the jobs
              you need in order to stay alive as a business.”
            </Heading>
            <Heading
              as="p"
              marginBottom={500}
              isDisplay
              size={200}
              color="neutral.0"
            >
              With offices in Montana, Wyoming, and Colorado, Sprague does a lot
              of business with customers managing storm damage. The end-to-end
              service they offer includes navigating insurance claims, which can
              be a huge relief for stressed-out property owners.
            </Heading>
          </Box>
          <Box>
            <FadeInImage offSetY="40px">
              <StaticImage
                src="../../../v2/assets/images/customer_sprague_1.png"
                alt="Sprague Roofing"
              />
            </FadeInImage>
          </Box>
        </SimpleGrid>
        {/* ContentGrid End */}

        <CustomerQuote
          quoteText="We’re bringing them peace-of-mind knowing that they’re going to be able to rely on us to take care of the full scope of work. We’re there to help the homeowners bring their property back to pre-loss condition."
          quoteBy="JOSH S."
        />
      </Container>

      <SeeFirstHand
        handleOpenVideoModal={handleOpenVideoModal}
        heading="See first-hand how Josh brings peace-of-mind to homeowners."
        thumbnail1={thumbnail1}
        thumbnail2={thumbnail2}
        thumbnail3={thumbnail3}
        videoLinks={spragueVideoLinks}
      />

      <Container
        maxW={{ base: '100%', tablet: '4xl', desktop: '6xl' }}
        px={{ base: 6, tablet: 12 }}
        textAlign="left"
        alignItems="center"
        mx={0}
      >
        {/* ContentGrid2 Start */}
        <SimpleGrid
          columns={{ base: 1, desktop: 2 }}
          spacing={{ base: 500, desktop: 100 }}
          gap={{ base: 12, tablet: 24 }}
          mb={{ base: 900, desktop: 1000 }}
        >
          <Box flexDir="column">
            <Heading
              marginBottom={500}
              isDisplay
              size={400}
              fontWeight={600}
              color="neutral.0"
            >
              The Right Tech Tools to Weather Any Storm
            </Heading>
            <Heading
              as="p"
              marginBottom={500}
              isDisplay
              size={200}
              color="neutral.0"
            >
              Hail can damage any part of a building’s exterior, from siding to
              windows to paint. And storms are often unpredictable, sometimes
              significantly impacting one portion of a structure while leaving
              others untouched. Using HOVER, Sprague has had the flexibility to
              quickly and easily capture multiple scopes of work across
              different types of storm damage.
            </Heading>
            <Heading
              as="p"
              marginBottom={500}
              isDisplay
              size={200}
              color="neutral.0"
            >
              {`“It allows us to pick and choose which facets of the house are
              actually impacted and allows us to estimate those a little bit
              easier," Sierra says. “So it saves me a ton of time and allows me
              to be a lot more efficient and effective as a manager.”`}
            </Heading>
            <Heading
              as="p"
              marginBottom={500}
              isDisplay
              size={200}
              color="neutral.0"
            >
              Getting accurate measurements is essential when filing insurance
              claims, and any discrepancies can quickly eat into a contractor’s
              profits. Even the time it takes to resolve such a dispute can be a
              time-consuming burden.
            </Heading>
          </Box>
          <Box flexDir="column" gap="12">
            <FadeInImage offSetY="40px">
              <StaticImage
                src="../../../v2/assets/images/customer_sprague_2.png"
                alt="Sprague Roofing"
              />
            </FadeInImage>
          </Box>
        </SimpleGrid>
        {/* ContentGrid2 End */}

        <Box mb={{ base: 900, desktop: 1000 }}>
          <CustomerQuote
            quoteText="If we didn’t have HOVER, we’d have to go out and spend more time at the job site hand-measuring, taking photos, and sending those in to prove our case."
            quoteBy="JOSH S."
          />
        </Box>

        {/* ContentGrid3 Start */}
        <SimpleGrid
          columns={{ base: 1, desktop: 2 }}
          spacing={{ base: 500, desktop: 100 }}
          gap={{ base: 12, tablet: 24 }}
          mb={{ base: 900, desktop: 1000 }}
        >
          <Box flexDir="column">
            <Heading
              marginTop={200}
              marginBottom={500}
              isDisplay
              size={400}
              fontWeight={600}
              color="neutral.0"
            >
              We Are Living in a Material World
            </Heading>
            <Heading
              as="p"
              marginBottom={500}
              isDisplay
              size={200}
              color="neutral.0"
            >
              Accuracy also takes center stage when it comes to materials
              ordering, which has become more difficult in recent years in the
              wake of widespread shortages.
            </Heading>
            <Heading
              as="p"
              marginBottom={500}
              isDisplay
              size={200}
              color="neutral.0"
            >
              “Accuracy is extremely important to us. Not only does it allow us
              to minimize our cost, but it ensures we have the correct amount of
              materials onsite to complete the job in full,” Sierra says.
            </Heading>
            <Heading
              as="p"
              marginBottom={500}
              isDisplay
              size={200}
              color="neutral.0"
            >
              “Anytime we’re short on materials, or we don’t have the right
              materials at the site, it means slowing down our production. A lot
              of times that creates an unhappy customer.”
            </Heading>
          </Box>
          <Box flexDir="column">
            <FadeInImage offSetY="40px">
              <StaticImage
                src="../../../v2/assets/images/customer_sprague_3.png"
                alt="Sprague Roofing"
              />
            </FadeInImage>
          </Box>
        </SimpleGrid>
        {/* ContentGrid3 End */}
      </Container>

      <Box
        w="100vw"
        maxW="100vw"
        maxHeight="300px"
        minHeight="300px"
        overflowX="auto"
        display="flex"
        justifyContent="center"
        mb={{ base: 900, desktop: 1000 }}
      >
        <Box flexShrink={2}>
          <StaticImage
            loading="lazy"
            placeholder="blurred"
            src="../../../v2/assets/images/customer_sprague_showcase_1.png"
            alt="Sprague Roofing Showcase Image"
          />
        </Box>
        <Box flexShrink={{ base: 1.5, desktop: 1.8 }}>
          <StaticImage
            loading="lazy"
            placeholder="blurred"
            src="../../../v2/assets/images/customer_sprague_showcase_2.png"
            alt="Sprague Roofing Showcase Image"
          />
        </Box>
        <Box flexShrink={2}>
          <StaticImage
            loading="lazy"
            placeholder="blurred"
            src="../../../v2/assets/images/customer_sprague_showcase_3.png"
            alt="Sprague Roofing Showcase Image"
          />
        </Box>
      </Box>

      <TryHover
        heading="Try the suite of tools Sprague uses to earn the trust of customers."
        cta={{
          label: 'START FREE TRIAL',
          href: () => toggleModal(),
        }}
      />

      <Container
        maxW={{ base: '100%', tablet: '4xl', desktop: '6xl' }}
        px={{ base: 6, tablet: 12 }}
        textAlign="left"
        alignItems="center"
        mx={0}
        mb={{ base: 600, tablet: 1000 }}
      >
        <BackButton />
      </Container>
    </Layout>
  );
};

export default CustomerStorySprague;
